import { apiRequest } from '../api';
import { state } from '../state';
import { navigateTo } from '../router';
import { t } from '../i18n';
import {
  PLAYER_REACTIONS as REACTIONS,
  paintCardReactions,
  type PlayerReactionsPayload,
} from './player-reactions-card';
import { attachWhoTooltip } from './admin-who';

// ============================================================
// Реакции на игрока под его карточкой: набор «отношения» к игроку.
// Один пользователь может отметить несколько разных реакций,
// повторный клик снимает свою. Блок крупный и читаемый — это
// не голосование за один вариант, а живой портрет отношения лиги.
// ============================================================

let currentPlayerId: number | null = null;
let payload: PlayerReactionsPayload | null = null;
let busy = false;
let errorText = '';

function goLogin(): void {
  sessionStorage.setItem(
    'redirectAfterLogin',
    window.location.pathname + window.location.search,
  );
  navigateTo('/auth');
}

/** Сбрасывает сводку при переходе на другую карточку (чужие реакции не мигают). */
export function resetPlayerReactions(): void {
  currentPlayerId = null;
  payload = null;
  busy = false;
  errorText = '';
}

/** Загружает реакции игрока и рисует блок (скелетон не нужен — кнопки видны сразу). */
export async function loadPlayerReactions(playerId: number): Promise<void> {
  const container = document.getElementById('player-reactions-container');
  if (!container) return;

  currentPlayerId = playerId;
  payload = null;
  errorText = '';
  renderPlayerReactions();

  try {
    payload = await apiRequest<PlayerReactionsPayload>(
      `/api/players/${playerId}/reactions`,
      { token: state.token },
    );
  } catch (err) {
    console.warn('[player-reactions] load error:', err);
    payload = { available: false };
  }
  renderPlayerReactions();
}

/** Перерисовка с текущими данными (в т.ч. при смене языка). */
export function renderPlayerReactions(): void {
  const container = document.getElementById('player-reactions-container');
  if (!container || currentPlayerId === null) return;

  container.innerHTML = '';

  // Таблицы ещё нет — не ломаем страницу.
  if (payload && !payload.available) {
    container.classList.add('hidden');
    renderCardReactions();
    return;
  }
  container.classList.remove('hidden');

  const section = document.createElement('section');
  section.className = 'player-reactions';
  section.setAttribute('aria-label', t('player.reactions_title'));

  // --- Шапка: заголовок и общее число реакций -----------------
  const head = document.createElement('div');
  head.className = 'player-reactions-head';

  const title = document.createElement('h3');
  title.className = 'player-reactions-title';
  title.textContent = t('player.reactions_title');

  const total = payload?.total ?? 0;
  const totalEl = document.createElement('span');
  totalEl.className = 'player-reactions-total';
  totalEl.textContent = total > 0 ? t('player.reactions_total', { n: total }) : '';

  head.append(title, totalEl);
  section.appendChild(head);

  // --- Кнопки реакций ----------------------------------------
  const loaded = payload !== null;
  const counts = payload?.counts ?? {};
  const my = new Set(payload?.my ?? []);

  const grid = document.createElement('div');
  grid.className = 'player-reactions-grid';
  grid.setAttribute('role', 'group');

  for (const item of REACTIONS) {
    const active = my.has(item.kind);
    const count = counts[item.kind] ?? 0;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `pr-btn pr-btn--${item.tone}${active ? ' is-active' : ''}`;
    btn.disabled = busy || !loaded;
    btn.title = t(item.key);
    btn.setAttribute('aria-pressed', String(active));

    const emoji = document.createElement('span');
    emoji.className = 'pr-emoji';
    emoji.setAttribute('aria-hidden', 'true');
    emoji.textContent = item.icon;

    const label = document.createElement('span');
    const labelText = t(item.key);
    label.className = 'pr-label' + (labelText.length > 11 ? ' pr-label--long' : '');
    label.textContent = labelText;

    const countEl = document.createElement('span');
    countEl.className = 'pr-count';
    countEl.textContent = loaded ? String(count) : '·';

    btn.append(emoji, label, countEl);
    btn.addEventListener('click', () => {
      if (!state.token) return goLogin();
      void send(item.kind);
    });
    attachWhoTooltip(btn, 'player', currentPlayerId, (who) => {
      const names = (who.reactions[item.kind] ?? []).map((p) => p.username).filter(Boolean);
      return names.length > 0 ? names.join(', ') : null;
    });
    grid.appendChild(btn);
  }

  section.appendChild(grid);

  // --- Итог: самая частая реакция -----------------------------
  const topCount = Math.max(0, ...Object.values(counts));
  if (topCount >= 2) {
    const top = REACTIONS.find((r) => (counts[r.kind] ?? 0) === topCount);
    if (top) {
      const verdict = document.createElement('p');
      verdict.className = 'player-reactions-verdict';
      verdict.textContent =
        `${t('player.reactions_verdict')}: ${top.icon} ${t(top.key)} · ${topCount}`;
      section.appendChild(verdict);
    }
  }

  // --- Подсказка / вход ---------------------------------------
  const note = document.createElement('p');
  note.className = 'player-reactions-note';

  if (!state.token) {
    const link = document.createElement('a');
    link.href = '#';
    link.className = 'hint-link';
    link.textContent = t('player.reactions_login');
    link.addEventListener('click', (e) => {
      e.preventDefault();
      goLogin();
    });
    note.appendChild(link);
  } else {
    note.textContent = t('player.reactions_hint');
  }

  if (errorText) note.textContent = errorText;
  section.appendChild(note);

  container.appendChild(section);

  // Краткая сводка на самой карточке игрока.
  renderCardReactions();
}

/**
 * Краткие реакции на карточке: топ-4 под легендой статов, над метриками.
 * Вызывается при загрузке/изменении сводки и при пересборке карточки.
 */
export function renderCardReactions(): void {
  paintCardReactions(payload, document, currentPlayerId);
}

async function send(kind: string): Promise<void> {
  if (busy || currentPlayerId === null) return;
  busy = true;
  errorText = '';
  renderPlayerReactions();

  try {
    payload = await apiRequest<PlayerReactionsPayload>(
      `/api/players/${currentPlayerId}/reactions`,
      { method: 'POST', token: state.token, body: { kind } },
    );
  } catch (err) {
    console.warn('[player-reactions] save failed:', err);
    errorText = t('player.reactions_error');
  } finally {
    busy = false;
    renderPlayerReactions();
  }
}
